// Shared by ResumeContentViewer and ResumeEditor so the on-screen resume, the
// editable resume and the PDF render from one set of rules. Both mount at once
// inside ResumeReviewModal, so keeping this in one place also stops two
// near-identical global style blocks fighting over `.ProseMirror`.
const RESUME_DOCUMENT_BASE_STYLES = `
    .ProseMirror {
        font-family: Cambria, Georgia, 'Times New Roman', serif;
        font-size: 11pt;
        line-height: 1.4;
        color: #000;
    }
    .ProseMirror h1 {
        font-size: 1.6em;
        font-weight: 700;
        text-align: center;
    }
    .ProseMirror h2 {
        font-size: 1.15em;
        font-weight: 700;
        font-variant: small-caps;
        border-bottom: 1px solid #000;
        padding-bottom: 2px;
        margin-top: 1.2em;
    }
    .ProseMirror h3 { font-size: 1.05em; font-weight: 700; }
    .ProseMirror ul, .ProseMirror ol { padding-left: 1.4em; }
    .ProseMirror ul li::marker { content: '▪  '; }
    .ProseMirror mark {
        background-color: #fdf2b3;
        padding: 0 1px;
    }
    /* A floated trailing date must not escape the container: html2canvas
       sizes the PDF canvas from this element, so an escaped float would be
       cropped out of the export. */
    .ProseMirror {
        display: flow-root;
    }
    /* Explicit paragraph spacing so the float math below (margin-top vs.
       line-height) isn't fighting an unpredictable browser default margin. */
    .ProseMirror p {
        margin: 0 0 0.6em 0;
    }
    /* Entry header rows (e.g. "Company, City | Role" + its right-aligned
       date range) are authored as two adjacent paragraphs with no wrapper
       node joining them. EntryDateParagraph marks only those date
       paragraphs with data-entry-date, so this never floats an unrelated
       right-aligned paragraph. Pulling the date up by one line-height plus
       the name's bottom margin (floats never collapse margins) puts it on
       the same visual line as the name paragraph;
       verified this also survives html2canvas's DOM-clone-and-rasterize
       step for the PDF export, not just the live view. */
    .ProseMirror p + p[data-entry-date="true"] {
        float: right;
        margin-top: -2em;
        margin-bottom: 0.6em;
        margin-left: 1em;
    }
    /* Headings, lists, and a plain (non-date) paragraph all start a new
       visual row, so they must clear the floated date above them —
       otherwise back-to-back entries with no bullet list between them
       drift out of alignment. The date paragraph itself is excluded: it's
       also a bare "p", but clearing it would push it below the name line
       instead of beside it, reintroducing the gap this rule removes. */
    .ProseMirror h1,
    .ProseMirror h2,
    .ProseMirror h3,
    .ProseMirror ul,
    .ProseMirror ol,
    .ProseMirror p:not([data-entry-date="true"]) {
        clear: both;
    }
    /* Entry headers authored with the entryHeader node (see
       EntryHeaderExtension.js): a real flex row, so the date lines up next
       to the name regardless of how long either side's text is — including
       a name that wraps to two lines, which the float rule above can't
       handle cleanly. The old float rule stays for resumes that still have
       the legacy two-paragraph pattern; both can coexist in the same doc. */
    .ProseMirror div[data-type="entry-header"] {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 1em;
        margin-bottom: 0.6em;
        clear: both;
    }
    .ProseMirror div[data-type="entry-header-name"] {
        flex: 1 1 auto;
    }
    .ProseMirror div[data-type="entry-header-date"] {
        flex: 0 0 auto;
        text-align: right;
        white-space: nowrap;
    }
`;

export const RESUME_VIEWER_STYLES = RESUME_DOCUMENT_BASE_STYLES;

export const RESUME_EDITOR_STYLES = `${RESUME_DOCUMENT_BASE_STYLES}
    .ProseMirror {
        min-height: 200px;
        outline: none;
    }
`;
